import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AgentAvailabilityResponse, AgentPresenceStatus } from '../models/call-routing.model';
import { CallAvailabilityApiService } from './call-availability-api.service';
import { VoiceDeviceService } from './voice-device.service';

/**
 * Singleton de presencia del agente actual.
 *
 * - Mantiene `presence$` con el último estado conocido (o `null` si no se cargó).
 * - Cuando el `VoiceDeviceService.deviceState$` transiciona a `READY` y el agente
 *   no había elegido manualmente `BUSY` / `ON_BREAK`, marca `AVAILABLE` para que
 *   pueda recibir inbound automáticamente.
 * - Cuando el Device se apaga, marca `OFFLINE` *best-effort*.
 * - El flag `manualPresence` evita pelearle al agente: si él/ella eligió BUSY o
 *   ON_BREAK desde el selector, el auto-AVAILABLE queda inhibido hasta que el
 *   agente reactive manualmente.
 */
@Injectable({ providedIn: 'root' })
export class PresenceService {
  private readonly api = inject(CallAvailabilityApiService);
  private readonly device = inject(VoiceDeviceService);

  private readonly _presence = new BehaviorSubject<AgentAvailabilityResponse | null>(null);
  readonly presence$ = this._presence.asObservable();

  /** True si el agente forzó manualmente BUSY u ON_BREAK — bloquea auto-AVAILABLE. */
  private manualPresence = false;
  private wired = false;

  get current(): AgentAvailabilityResponse | null { return this._presence.value; }
  get status(): AgentPresenceStatus | null { return this._presence.value?.presenceStatus ?? null; }

  /**
   * Inicializa el servicio: pide la presencia actual y se suscribe al estado del
   * Device para auto-cambios. Idempotente.
   */
  init(): void {
    if (this.wired) return;
    this.wired = true;

    this.refresh().subscribe();

    this.device.deviceState$.subscribe((s) => {
      if (s === 'READY' && !this.manualPresence) {
        // Solo auto-marcar AVAILABLE si la última presencia no es ya AVAILABLE.
        if (this.status !== 'AVAILABLE') this.update('AVAILABLE', { silent: true }).subscribe();
      } else if (s === 'OFFLINE') {
        if (this.status && this.status !== 'OFFLINE') {
          this.update('OFFLINE', { silent: true }).subscribe();
        }
      }
    });
  }

  refresh(): Observable<AgentAvailabilityResponse | null> {
    return this.api.getMyAvailability().pipe(
      tap((res) => this._presence.next(res)),
      catchError(() => of(null))
    );
  }

  /**
   * Cambia la presencia. `manual=true` marca el flag para inhibir auto-AVAILABLE.
   * `silent=true` evita marcar `manualPresence` (uso interno del auto-cambio).
   */
  update(status: AgentPresenceStatus, opts: { manual?: boolean; silent?: boolean } = {}): Observable<AgentAvailabilityResponse | null> {
    return this.api.updateMyAvailability({ presenceStatus: status }).pipe(
      tap((res) => {
        this._presence.next(res);
        if (opts.manual) {
          this.manualPresence = (status === 'BUSY' || status === 'ON_BREAK');
        } else if (!opts.silent) {
          // Llamada externa sin marcar manual ni silent: trata como manual moderado.
          this.manualPresence = (status === 'BUSY' || status === 'ON_BREAK');
        }
      }),
      catchError(() => of(null))
    );
  }
}
